import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ServiceLayout } from '@/components/ServiceLayout';
import { Plus, Pencil, Trash2, Save, X, Search, Loader2, Settings, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { broadcastMastersChanged } from '@/hooks/useServiceMasters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// type-key configuration maps URL slug → masters API type + label + fields
const MASTER_TYPES = {
  'status': { type: 'status', label: 'Status', simple: true },
  'service-head': { type: 'service_head', label: 'Service Head', simple: true },
  'job-category': { type: 'job_category', label: 'Job Category', simple: true },
  'job-type': { type: 'job_type', label: 'Job Type', simple: true },
  'customer-type': { type: 'customer_type', label: 'Customer Type', simple: true },
  'supervisor': {
    type: 'service_supervisor', label: 'Supervisor', simple: false,
    fields: [
      { key: 'name', label: 'Supervisor Name', required: true },
      { key: 'emp_mobile', label: 'Mobile No' },
      { key: 'emp_email', label: 'Email' },
      { key: 'emp_door_no', label: 'Door No' },
      { key: 'emp_address_1', label: 'Address Line 1' },
      { key: 'emp_address_2', label: 'Address Line 2' },
      { key: 'area', label: 'Area' },
      { key: 'emp_city', label: 'City' },
      { key: 'emp_state', label: 'State' },
      { key: 'emp_pincode', label: 'Pin Code' },
    ],
  },
  'technician': {
    type: 'service_technician', label: 'Technician', simple: false,
    fields: [
      { key: 'name', label: 'Technician Name', required: true },
      { key: 'emp_mobile', label: 'Mobile No' },
      { key: 'emp_email', label: 'Email' },
      { key: 'emp_door_no', label: 'Door No' },
      { key: 'emp_address_1', label: 'Address Line 1' },
      { key: 'emp_address_2', label: 'Address Line 2' },
      { key: 'area', label: 'Area' },
      { key: 'emp_city', label: 'City' },
      { key: 'emp_state', label: 'State' },
      { key: 'emp_pincode', label: 'Pin Code' },
    ],
  },
};

const inputCx = 'w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-[14px] text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all';

export default function ServiceMasters({ user, onLogout }) {
  const { type: typeSlug } = useParams();
  const navigate = useNavigate();
  const cfg = MASTER_TYPES[typeSlug] || MASTER_TYPES['status'];

  // Filter-visibility toggle is admin-write only; matches the Service Settings pattern.
  const isAdmin = (user?.role_name || '').trim() === 'Company Admin';

  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [editing, setEditing] = React.useState(null); // null | "new" | item
  // Per-row pending flag so we disable the toggle while the PUT is in flight
  const [togglingId, setTogglingId] = React.useState(null);

  const fetchItems = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/masters?type=${cfg.type}&limit=2000`);
      setItems(data || []);
    } catch {
      toast.error(`Failed to load ${cfg.label}`);
    } finally {
      setLoading(false);
    }
  }, [cfg.type, cfg.label]);

  React.useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => Object.values(it).some((v) => String(v || '').toLowerCase().includes(q)));
  }, [items, search]);

  const openCreate = () => setEditing({ _new: true, type: cfg.type, name: '' });
  const openEdit = (item) => setEditing({ ...item });
  const closeForm = () => setEditing(null);

  const handleSave = async () => {
    if (!editing.name?.trim()) { toast.error('Name is required'); return; }
    try {
      const payload = { ...editing, type: cfg.type };
      delete payload._new;
      delete payload.id;
      delete payload.created_at;
      if (editing._new) {
        await axios.post(`${API}/masters`, payload);
        toast.success(`${cfg.label} created`);
        broadcastMastersChanged({ type: cfg.type, action: 'create' });
      } else {
        await axios.put(`${API}/masters/${editing.id}`, payload);
        toast.success(`${cfg.label} updated`);
        broadcastMastersChanged({ type: cfg.type, action: 'update' });
      }
      closeForm();
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/masters/${item.id}`);
      toast.success('Deleted');
      broadcastMastersChanged({ type: cfg.type, action: 'delete' });
      fetchItems();
    } catch {
      toast.error('Delete failed');
    }
  };

  /**
   * Flip the per-record `is_filter_visible` flag. Optimistic update so the
   * chip flips instantly; PUT payload re-sends the whole record because the
   * existing /masters/{id} endpoint uses MasterDataCreate (full replace).
   * On failure we revert and surface the error.
   */
  const handleToggleFilterVisibility = async (item) => {
    if (!isAdmin) return;
    const nextVisible = item.is_filter_visible === false; // flipping false → true, else → false
    setTogglingId(item.id);
    // Optimistic local update
    setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, is_filter_visible: nextVisible } : it)));
    try {
      const payload = { ...item, type: cfg.type, is_filter_visible: nextVisible };
      delete payload._new;
      delete payload.id;
      delete payload.created_at;
      await axios.put(`${API}/masters/${item.id}`, payload);
      toast.success(nextVisible ? 'Now visible in filters' : 'Hidden from filters');
      broadcastMastersChanged({ type: cfg.type, action: 'update' });
    } catch (err) {
      // Revert
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, is_filter_visible: !nextVisible } : it)));
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setTogglingId(null);
    }
  };

  // Service master types eligible for "Sync from existing jobs" backfill
  const BACKFILLABLE = new Set(['status', 'service_head', 'job_category', 'job_type', 'customer_type', 'service_supervisor', 'service_technician']);
  const [syncing, setSyncing] = React.useState(false);

  const handleSync = async () => {
    if (!BACKFILLABLE.has(cfg.type)) return;
    if (!window.confirm(
      `Sync ${cfg.label} from existing Service Jobs?\n\n` +
      `This will scan all existing service jobs and create master records for any ` +
      `${cfg.label.toLowerCase()} value not already present. Existing entries are preserved.`
    )) return;
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/service/masters/backfill?type=${cfg.type}`);
      if (data.created === 0) {
        toast.success(`No new ${cfg.label} entries to add — all ${data.scanned} values already in master.`);
      } else {
        toast.success(`Synced ${data.created} new ${cfg.label} entries from ${data.scanned} unique value(s) found in jobs.`);
        broadcastMastersChanged({ type: cfg.type, action: 'sync', created: data.created });
      }
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const tabs = Object.entries(MASTER_TYPES).map(([slug, c]) => ({ slug, label: c.label }));

  const headerActions = (
    <div className="flex items-center gap-2">
      {BACKFILLABLE.has(cfg.type) && (
        <button
          onClick={handleSync}
          disabled={syncing}
          data-testid="master-sync-btn"
          title={`Import existing ${cfg.label} values from Service Jobs into the master`}
          className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-[13px] font-medium flex items-center gap-2 disabled:opacity-60"
        >
          {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Sync from Jobs
        </button>
      )}
      <button
        onClick={openCreate}
        data-testid="master-add-btn"
        className="px-4 py-2 rounded-xl text-white text-[13px] font-medium shadow-sm shadow-indigo-500/20 hover:shadow-md transition-all flex items-center gap-2"
        style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #DB2777 100%)' }}
      >
        <Plus size={15} /> Add {cfg.label}
      </button>
    </div>
  );

  return (
    <ServiceLayout user={user} onLogout={onLogout} title="Masters" subtitle={`${cfg.label} · ${items.length} entries`} actions={headerActions}>
      <div className="grid md:grid-cols-[220px_1fr] gap-5" data-testid="service-masters-page">
        {/* Type sidebar */}
        <aside className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 p-2.5 h-fit sticky top-24">
          <div className="px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <Settings size={12} /> Master Types
          </div>
          {tabs.map((t) => {
            const active = t.slug === typeSlug;
            return (
              <button
                key={t.slug}
                onClick={() => navigate(`/service/masters/${t.slug}`)}
                data-testid={`master-tab-${t.slug}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-[13px] mb-0.5 transition-all
                  ${active ? 'bg-gradient-to-r from-indigo-500/10 via-violet-500/10 to-fuchsia-500/8 text-indigo-700 ring-1 ring-indigo-200/60 font-medium' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`}
              >
                {t.label}
              </button>
            );
          })}
        </aside>

        {/* List */}
        <div className="rounded-2xl bg-white/75 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${cfg.label}…`}
                data-testid="master-search"
                className="w-full pl-9 pr-3 py-2 rounded-xl bg-white border border-slate-200 text-[13px] focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center"><Loader2 size={22} className="animate-spin text-indigo-500 mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-[13px]">
              No {cfg.label} entries. <button onClick={openCreate} className="text-indigo-600 hover:underline font-medium">Add the first one</button>.
            </div>
          ) : cfg.simple ? (
            <ul className="divide-y divide-slate-100">
              {filtered.map((it) => (
                <li key={it.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50/60 transition-colors">
                  <span className="flex-1 text-[14px] text-slate-800">{it.name}</span>
                  <div className="flex items-center gap-1.5">
                    {isAdmin && (
                      <FilterVisibilityToggle
                        item={it}
                        busy={togglingId === it.id}
                        onToggle={() => handleToggleFilterVisibility(it)}
                      />
                    )}
                    <button onClick={() => openEdit(it)} title="Edit" data-testid={`master-edit-${it.id}`} className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:text-indigo-700 hover:border-indigo-200 hover:bg-indigo-50">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => handleDelete(it)} title="Delete" data-testid={`master-delete-${it.id}`} className="p-1.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left bg-slate-50/80 border-b border-slate-200">
                    {cfg.fields.slice(0, 5).map((f) => (
                      <th key={f.key} className="p-3 font-semibold text-slate-600">{f.label}</th>
                    ))}
                    <th className="p-3 text-right font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                      {cfg.fields.slice(0, 5).map((f) => (
                        <td key={f.key} className="p-3 text-slate-800">{it[f.key] || '—'}</td>
                      ))}
                      <td className="p-3 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          {isAdmin && (
                            <FilterVisibilityToggle
                              item={it}
                              busy={togglingId === it.id}
                              onToggle={() => handleToggleFilterVisibility(it)}
                            />
                          )}
                          <button onClick={() => openEdit(it)} title="Edit" data-testid={`master-edit-${it.id}`} className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:text-indigo-700 hover:border-indigo-200 hover:bg-indigo-50">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => handleDelete(it)} title="Delete" data-testid={`master-delete-${it.id}`} className="p-1.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit / Add Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={closeForm} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-white/50 w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" data-testid="master-edit-modal">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-[16px] font-semibold text-slate-900">{editing._new ? `Add ${cfg.label}` : `Edit ${cfg.label}`}</h3>
                <p className="text-[12px] text-slate-500">{cfg.simple ? 'Provide a unique name.' : 'Fill in the details.'}</p>
              </div>
              <button onClick={closeForm} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={16} /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cfg.simple ? (
                <div className="md:col-span-2">
                  <label className="block text-[12px] font-medium text-slate-600 mb-1">Name <span className="text-rose-500">*</span></label>
                  <input
                    value={editing.name || ''}
                    onChange={(e) => setEditing((x) => ({ ...x, name: e.target.value }))}
                    placeholder={`Enter ${cfg.label}`}
                    autoFocus
                    data-testid="master-name-input"
                    className={inputCx}
                  />
                </div>
              ) : (
                cfg.fields.map((f) => (
                  <div key={f.key} className={f.key === 'name' ? 'md:col-span-2' : ''}>
                    <label className="block text-[12px] font-medium text-slate-600 mb-1">
                      {f.label} {f.required && <span className="text-rose-500">*</span>}
                    </label>
                    <input
                      value={editing[f.key] || ''}
                      onChange={(e) => setEditing((x) => ({ ...x, [f.key]: e.target.value }))}
                      data-testid={`master-field-${f.key}`}
                      className={inputCx}
                    />
                  </div>
                ))
              )}
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={closeForm} className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-[13px]">Cancel</button>
              <button onClick={handleSave} data-testid="master-save-btn" className="px-4 py-2 rounded-xl text-white text-[13px] font-medium flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #DB2777 100%)' }}>
                <Save size={14} /> {editing._new ? 'Create' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ServiceLayout>
  );
}

/**
 * Compact pill toggle for per-record "Show in Filters". Admin-only (caller
 * gates rendering). Rendered inline next to Edit / Delete on every master row.
 *
 * - Visible (default)  : green eye + "Show in Filters" label — clicking hides
 * - Hidden             : amber eye-off + "Hidden from Filters" — clicking shows
 *
 * Absent `is_filter_visible` on legacy docs is treated as visible (backend
 * coerces to True in the GET response).
 */
function FilterVisibilityToggle({ item, busy, onToggle }) {
  const visible = item.is_filter_visible !== false;
  const Icon = visible ? Eye : EyeOff;
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={busy}
      title={visible ? 'Visible in filter dropdowns — click to hide' : 'Hidden from filter dropdowns — click to show'}
      aria-label="Show in Filters"
      data-testid={`master-filter-visibility-${item.id}`}
      data-state={visible ? 'on' : 'off'}
      className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border text-[11.5px] font-medium transition-colors disabled:opacity-60 ${
        visible
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
          : 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
      }`}
    >
      <Icon size={13} />
      <span className="hidden sm:inline">{visible ? 'Show in Filters' : 'Hidden'}</span>
    </button>
  );
}

